import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const REPORT_TYPES = [
  { id: 'sales-account', label: 'Sales by Account', icon: 'Users', description: 'Detailed account-level sales breakdown' },
  { id: 'product-performance', label: 'Product Performance', icon: 'Package', description: 'SKU-level sales and volume analysis' },
  { id: 'territory-ranking', label: 'Territory Rankings', icon: 'Map', description: 'Comparative territory performance' },
  { id: 'salesperson-productivity', label: 'Salesperson Productivity', icon: 'UserCheck', description: 'Individual rep performance metrics' },
  { id: 'inventory-correlation', label: 'Inventory vs Sales', icon: 'BarChart2', description: 'Stock correlation and turnover analysis' },
  { id: 'ai-forecast', label: 'AI Forecast Report', icon: 'BrainCircuit', description: 'Predictive analytics and projections' },
];

const DATE_RANGES = [
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'last90', label: 'Last 90 Days' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom Range' },
];

const FORMATS = [
  { id: 'excel', label: 'Excel', icon: 'FileSpreadsheet', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  { id: 'pdf', label: 'PDF', icon: 'FileText', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  { id: 'csv', label: 'CSV', icon: 'FileCode', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
];

export default function ReportExporter() {
  const [selectedReports, setSelectedReports] = useState(['sales-account']);
  const [selectedFormat, setSelectedFormat] = useState('excel');
  const [dateRange, setDateRange] = useState('last30');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  const toggleReport = (id) => {
    setSelectedReports((prev) =>
      prev?.includes(id) ? prev?.filter((r) => r !== id) : [...prev, id]
    );
    setGenerated(false);
  };

  const handleGenerate = () => {
    if (!selectedReports?.length) return;
    setGenerating(true);
    setGenerated(false);
    setTimeout(() => {
      setGenerating(false);
      setGenerated(true);
    }, 2200);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center justify-center rounded-lg" style={{ width: 40, height: 40, background: 'var(--color-primary)18' }}>
          <Icon name="Download" size={20} color="var(--color-primary)" strokeWidth={2} />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">Report Generator</h3>
          <p className="font-caption text-xs text-muted-foreground">Export comprehensive reports in multiple formats</p>
        </div>
      </div>
      {/* Report Type Selection */}
      <div className="mb-5">
        <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Select Reports</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {REPORT_TYPES?.map((rt) => {
            const selected = selectedReports?.includes(rt?.id);
            return (
              <button
                key={rt?.id}
                onClick={() => toggleReport(rt?.id)}
                className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all duration-150 ${
                  selected
                    ? 'border-primary bg-blue-50' :'border-border bg-background hover:bg-muted'
                }`}
              >
                <div className={`flex items-center justify-center rounded-md flex-shrink-0 ${selected ? 'bg-primary' : 'bg-muted'}`} style={{ width: 32, height: 32 }}>
                  <Icon name={rt?.icon} size={16} color={selected ? '#fff' : 'var(--color-muted-foreground)'} strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className={`font-caption text-sm font-medium ${selected ? 'text-primary' : 'text-foreground'}`}>{rt?.label}</p>
                  <p className="font-caption text-xs text-muted-foreground truncate">{rt?.description}</p>
                </div>
                {selected && <Icon name="CheckCircle" size={16} color="var(--color-primary)" strokeWidth={2} className="ml-auto flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      </div>
      {/* Date Range */}
      <div className="mb-5">
        <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Date Range</p>
        <div className="flex flex-wrap gap-2">
          {DATE_RANGES?.map((dr) => (
            <button
              key={dr?.value}
              onClick={() => setDateRange(dr?.value)}
              className={`px-3 py-1.5 rounded-lg border font-caption text-xs font-medium transition-all duration-150 ${
                dateRange === dr?.value
                  ? 'border-primary bg-blue-50 text-primary' :'border-border bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              {dr?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Format Selection */}
      <div className="mb-5">
        <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Export Format</p>
        <div className="flex gap-3">
          {FORMATS?.map((fmt) => (
            <button
              key={fmt?.id}
              onClick={() => setSelectedFormat(fmt?.id)}
              className={`flex-1 flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all duration-150 ${
                selectedFormat === fmt?.id ? `${fmt?.bg} ${fmt?.border}` : 'border-border bg-background hover:bg-muted'
              }`}
            >
              <Icon name={fmt?.icon} size={22} color={selectedFormat === fmt?.id ? fmt?.color?.replace('text-', '') : 'var(--color-muted-foreground)'} strokeWidth={2} />
              <span className={`font-caption text-xs font-semibold ${selectedFormat === fmt?.id ? fmt?.color : 'text-muted-foreground'}`}>{fmt?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Generate Button */}
      <div className="flex items-center gap-3">
        <Button
          variant="default"
          fullWidth
          loading={generating}
          iconName={generated ? 'CheckCircle' : 'Download'}
          iconPosition="left"
          iconSize={16}
          onClick={handleGenerate}
          disabled={!selectedReports?.length || generating}
        >
          {generating ? 'Generating Report...' : generated ? 'Download Ready' : `Generate ${selectedReports?.length} Report${selectedReports?.length !== 1 ? 's' : ''}`}
        </Button>
      </div>
      {generated && (
        <div className="mt-3 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Icon name="CheckCircle" size={16} color="var(--color-success)" strokeWidth={2} />
          <span className="font-caption text-xs text-emerald-700">
            {selectedReports?.length} report{selectedReports?.length !== 1 ? 's' : ''} generated successfully. Click Download Ready to save.
          </span>
        </div>
      )}
    </div>
  );
}